/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMsal } from "@azure/msal-react";
import { Config } from "config";
import { useMyDNVContext } from "mydnv/context/mydnv-context";
import { ConfirmOperationModal } from "mydnv/layout/full-width-myDNV/confirm-operation-modal";
import * as userService from "mydnv/services/user-service";
import { formatDisplayName } from "mydnv/utils/format-display-name";
import { RateMyExperience } from "mydnv/components/rate-my-experience";
import { MenuItem, Select } from "@mui/material";
import * as data from "./address-data";
import * as ratingService from "../../../services/rating-service";
import { solid } from "assets/icons";
const { faCheck, faTriangleExclamation } = solid;

export const AccountInfoPanel = ({ onUserDeleteRequested }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [province, setProvince] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [modalToggleState, setModalToggleState] = useState(false);
    const [showRating, setShowRating] = useState(true);
    const [userAddress, setUserAddress] = useState({
        streetAddress: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
    });
    const [nameBtnMsg, setNameBtnMsg] = useState("Update my information");
    const [nameSaveError, setNameSaveError] = useState(false);
    const [nameSaved, setNameSaved] = useState(false);
    const [addressBtnMsg, setAddressBtnMsg] = useState("Update my address");
    const [addressSaveError, setAddressSaveError] = useState(false);
    const [addressSaved, setAddressSaved] = useState(false);

    const postalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    const loginRequest = Config.MyDNV.loginRequest;

    const { instance } = useMsal();

    const { setUserFirstName, userAccount, setDisplayName, setUserAccount, account } = useMyDNVContext();

    useEffect(() => {
        // console.log(instance);
        setFirstName(userAccount?.first_Name || "");
        setUserFirstName(userAccount?.first_Name || "");
        setLastName(userAccount?.last_Name || "");
        setEmailAddress(userAccount?.emails[0] || "");
        // runApiCall(getLastRating);
        runApiCall(getUserAddress);
    }, [instance, userAccount, setUserFirstName]);

    const runApiCall = useCallback(
        (callback, ...args) => {
            const request = {
                ...loginRequest,
                account: account,
            };

            instance
                .acquireTokenSilent(request)
                .then((response) => {
                    if (args.length > 0) {
                        callback(response.accessToken, ...args);
                    } else {
                        callback(response.accessToken);
                    }
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        [account, instance, loginRequest],
    );

    const getLastRating = async (accessToken) => {
        try {
            let response = await ratingService.getLastRating(accessToken);
            setShowRating(response.data);
        } catch (e) {
            console.log(e);
        }
    };

    const getUserAddress = async (accessToken) => {
        try {
            await userService.getUserAddress(accessToken).then((response) => {
                setStreetAddress(response.data.street_Address);
                setCity(response.data.city);
                setCountry(response.data.country);
                setProvince(response.data.province);
                setPostalCode(response.data.postal_Code);
                setUserAddress({
                    streetAddress: response.data.street_Address,
                    city: response.data.city,
                    country: response.data.country,
                    province: response.data.province,
                    postalCode: response.data.postal_Code,
                });
            });
        } catch (e) {
            console.log(e);
        }
    };

    const handleSignOut = () => {
        instance.logoutRedirect({
            postLogoutRedirectUri: "/",
        });
    };

    const postUserDelete = async (token) => await userService.deleteUserAccount(token);
    const handleUserDelete = () => {
        runApiCall(postUserDelete);
        setTimeout(handleSignOut, 2000);
    };

    const putUserUpdate = async (token, changedFields) => {
        try {
            await userService
                .updateUserDetails(token, changedFields)
                .then((response) => {
                    if (response.status === 200) {
                        console.log(response);
                        setNameSaved(true);
                        setNameBtnMsg("Saved");
                        setNameSaveError(false);
                    }
                })
                .catch((e) => {
                    console.log("error: ", e);
                    setNameSaved(false);
                    setNameBtnMsg("Update my information");
                    setNameSaveError(true);
                });
        } catch (err) {
            console.log(err);
            setNameSaved(false);
            setNameBtnMsg("Update my information");
            setNameSaveError(true);
        }
    };

    const putAddressUpdate = async (token, addressObj) => {
        try {
            await userService
                .updateAddressDetails(token, addressObj)
                .then((response) => {
                    if (response.status === 200) {
                        console.log(response);
                        setAddressSaved(true);
                        setAddressBtnMsg("Saved");
                        setAddressSaveError(false);
                        setUserAddress({
                            streetAddress: addressObj.street_address,
                            city: addressObj.city,
                            country: addressObj.country,
                            province: addressObj.province,
                            postalCode: addressObj.postal_code,
                        });
                    }
                })
                .catch((e) => {
                    console.log("error: ", e);
                    setAddressSaved(false);
                    setAddressBtnMsg("Update my information");
                    setAddressSaveError(true);
                });
        } catch (err) {
            console.log(err);
            setAddressSaved(false);
            setAddressBtnMsg("Update my information");
            setAddressSaveError(true);
        }
    };

    const handleUpdateInfo = () => {
        onUserUpdate({ firstName, lastName, email: emailAddress });
    };

    const onUserUpdate = (userChanges) => {
        let newUser = {
            ...userAccount,
            first_Name: userChanges.firstName,
            last_Name: userChanges.lastName,
        };

        setDisplayName(formatDisplayName(userChanges.firstName, userChanges.lastName));
        setUserFirstName(userChanges.firstName);
        runApiCall(putUserUpdate, userChanges);
        setUserAccount(newUser);
        setNameBtnMsg("Saving...");
    };

    const handleUpdateAddress = () => {
        const addressObj = {
            street_address: streetAddress,
            city,
            country,
            province,
            postal_code: postalCode,
        };
        runApiCall(putAddressUpdate, addressObj);
        setAddressBtnMsg("Saving...");
    };

    const handleResetPassword = () => {
        instance.loginRedirect(Config.MyDNV.passwordResetRequest);
    };

    const openModal = () => setModalToggleState(true);

    const handleModal = (isDeleteRequested) => {
        if (isDeleteRequested) {
            onUserDeleteRequested();
        }
        setModalToggleState(false);
    };

    const handleNameInputChange = (e) => {
        // console.log(e);
        const inputVal = e.target.value;
        if (e.target.name === "firstName") {
            setFirstName(inputVal);
        } else {
            setLastName(inputVal);
        }
        setNameSaved(false);
        setNameBtnMsg("Update my information");
    };

    const handleAddressChange = (e) => {
        if (e.target.name === "country") {
            if (e.target.value !== "Canada") {
                setProvince("");
                setPostalCode("");
            }
        }
        setAddressSaved(false);
        setAddressBtnMsg("Update my information");
    };

    const nameBtnDisabled =
        userAccount?.first_Name === firstName.trim() &&
        firstName.trim().length > 0 &&
        userAccount?.last_Name === lastName.trim() &&
        lastName.trim().length > 0;

    const checkBlankAndDiff = (prevValue, currentValue) => {
        return currentValue.length > 0 && prevValue !== currentValue;
    };

    const addressBtnDisabled =
        !checkBlankAndDiff(userAddress.streetAddress, streetAddress) &&
        !checkBlankAndDiff(userAddress.city, city) &&
        !checkBlankAndDiff(userAddress.country, country) &&
        !checkBlankAndDiff(userAddress.province, province) &&
        !checkBlankAndDiff(userAddress.postalCode, postalCode);

    const handlePC = (e) => {
        const pc = e.target.value;
        if (pc.length > 3) {
            const temp = pc.replaceAll(" ", ""); // take out the space(s)
            const pcFormat = temp.substring(0, 3) + " " + temp.substring(3);
            setPostalCode(pcFormat.toUpperCase());
        } else {
            setPostalCode(pc.toUpperCase());
        }
    };

    const notCanada = country.length > 0 && country !== "Canada";

    return (
        <div className="account-info">
            <div className="accountsPanelCardContainer">
                <div className={`accountsLeftContainer ${nameSaveError || addressSaveError ? "error" : ""}`}>
                    <h2 className="cardTitle">Personal information</h2>
                    <div className="accountsPersonalInfo">
                        <div className="entryField">
                            <label>
                                First name
                                <input
                                    name="firstName"
                                    type="text"
                                    value={firstName}
                                    onChange={handleNameInputChange}
                                    onInput={(e) => {
                                        setFirstName(e.target.value);
                                    }}
                                />
                            </label>
                        </div>

                        <div className="entryField">
                            <label>
                                Last name
                                <input
                                    name="lastName"
                                    type="text"
                                    value={lastName}
                                    onChange={handleNameInputChange}
                                    onInput={(e) => {
                                        setLastName(e.target.value);
                                    }}
                                />
                            </label>
                        </div>

                        <div className="entryField">
                            <label>
                                Email address
                                <input type="text" value={emailAddress} disabled={true} />
                            </label>
                            <span className="lock-icon">
                                <FontAwesomeIcon icon="fas fa-lock" />
                            </span>
                            <div className="emailNoEditMsg">
                                Sorry, you can&apos;t update your email address... but we&apos;re working on it!
                            </div>
                        </div>

                        <button
                            className={`updateInfoBtn ${nameSaved ? "saved" : ""}`}
                            onClick={handleUpdateInfo}
                            disabled={nameBtnDisabled && !nameSaveError}
                        >
                            {nameBtnMsg}
                            {nameSaved && <FontAwesomeIcon icon={faCheck} />}
                        </button>
                        {nameSaveError && (
                            <div className="name-error">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                Something went wrong, please try again.
                            </div>
                        )}
                    </div>
                </div>

                <div className={`accountsRightContainer ${nameSaveError || addressSaveError ? "error" : ""}`}>
                    <h2 className="cardTitle">Address</h2>
                    <div className="accountsPersonalInfo">
                        <div className="entryField">
                            <label>
                                Street address
                                <input
                                    type="text"
                                    value={streetAddress}
                                    onChange={handleAddressChange}
                                    onInput={(e) => {
                                        setStreetAddress(e.target.value);
                                    }}
                                />
                            </label>
                        </div>

                        <div className="entryField">
                            <label>
                                City
                                <input
                                    type="text"
                                    value={city}
                                    onChange={handleAddressChange}
                                    onInput={(e) => {
                                        setCity(e.target.value);
                                    }}
                                />
                            </label>
                        </div>
                        <div className="row">
                            <div className={`entryField country ${notCanada ? "full-width" : ""}`}>
                                <label>
                                    Country
                                    <Select
                                        className="address-select"
                                        classes={{
                                            select: "select-country",
                                        }}
                                        name="country"
                                        label="Select country"
                                        defaultValue={""}
                                        value={country}
                                        onChange={(e) => {
                                            setCountry(e.target.value);
                                            handleAddressChange(e);
                                        }}
                                    >
                                        {data.countries.map((c) => (
                                            <MenuItem key={c} value={c} disableRipple>
                                                {c}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </label>
                            </div>
                            <div className={`entryField province ${notCanada ? "hidden" : ""}`}>
                                <label>
                                    Province
                                    <Select
                                        name="province"
                                        className="address-select"
                                        classes={{
                                            select: "select-province",
                                        }}
                                        label="Select province"
                                        defaultValue={""}
                                        value={province}
                                        onChange={(e) => {
                                            setProvince(e.target.value);
                                            handleAddressChange(e);
                                        }}
                                    >
                                        {data.provinces.map((p) => (
                                            <MenuItem key={p} value={p} disableRipple>
                                                {p}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </label>
                            </div>
                            <div className={`entryField postal ${notCanada ? "hidden" : ""}`}>
                                <label>
                                    Postal Code
                                    <input
                                        type="text"
                                        name="postalcode"
                                        value={postalCode}
                                        onInput={handlePC}
                                        maxLength={7}
                                        onChange={handleAddressChange}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="emailNoEditMsg">
                            This information can help you save time when completing forms.
                        </div>
                        <button
                            className={`updateInfoBtn ${addressSaved ? "saved" : ""}`}
                            onClick={handleUpdateAddress}
                            disabled={addressBtnDisabled && !addressSaveError}
                        >
                            {addressBtnMsg}
                            {addressSaved && <FontAwesomeIcon icon={faCheck} />}
                        </button>
                        {addressSaveError && (
                            <div className="name-error">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                Something went wrong, please try again.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showRating && <RateMyExperience></RateMyExperience>}

            <h2 className="cardTitle">Reset password</h2>
            <div className="accountsPanelCardContainer">
                <div className="accountsReset">
                    <div className="resetCardMessage">
                        Select this button to receive an email with instructions to create a new, secure password.
                    </div>
                    <button className="resetBtn" onClick={handleResetPassword}>
                        Send reset link
                    </button>
                </div>
            </div>

            <h2 className="cardTitle">Close account</h2>
            <div className="accountsPanelCardContainer">
                <div className="accountsDelete">
                    <div className="deleteCardMessage">
                        Select this button to close your account. You will no longer receive notifications.
                    </div>
                    <button className="deleteBtn" onClick={openModal}>
                        Close my account
                    </button>
                </div>
            </div>
            <ConfirmOperationModal
                isOpen={modalToggleState}
                setModalOpen={handleModal}
                headerText="Confirm account closure"
                paragraphText="Are you sure you want to close your MyDNV account?"
                okButtonText="Yes, close my account"
            />
        </div>
    );
};
